<template>
    <auth-layout
        :image="1"
        :byword="$t('pages.auth.signup.steps.register.byword')"
        :progress="progress"
        :isProgressBarHidden="true"
        :isPageLoading="isPagePreparing"
    >
        <common-content-wrapper width="small">
            <common-page-header :heading="getHeading">
                <template
                    v-if="dataForPrefill.practiceName && invitee?.inviteType !== JoinInviteTypeEnum.REFERRAL_CREATION"
                    #content
                >
                    {{ $t('pages.auth.signup.steps.register.description.invite') }}
                    <br />
                    <b>{{ dataForPrefill.practiceName }}</b>
                </template>
                <template v-else-if="invitee?.inviteType !== JoinInviteTypeEnum.REFERRAL_CREATION" #content>
                    {{ $t('pages.auth.signup.steps.register.description.or') }}
                    <router-link :to="{ name: 'auth.login' }" :class="loginLinkClass">
                        {{ $t('pages.auth.signup.steps.register.description.login') }}
                    </router-link>
                </template>
            </common-page-header>
            <common-modal v-if="billingPricingModalOpen" width="extra-large" @close="toggleBillingPricingModal">
                <div class="pricing-cards">
                    <pricing-cards :showButtons="false" @upgrade="showPricingDetails = !showPricingDetails"></pricing-cards>
                </div>
            </common-modal>
        </common-content-wrapper>

        <common-content-wrapper width="small">
            <auth-register-with-npi-form
                v-if="isSignUpWithNpiEnabled"
                @go-wtihout-npi="goWithoutNpi"
                @provider-data-matched="handleProviderDataMatched"
            ></auth-register-with-npi-form>
            <register-form
                v-else
                :organizationRoles="filteredOrganizationRoles"
                :specialities="specialities"
                :physicianGroups="physicianGroups"
                :dataForPrefill="dataForPrefill"
                :isComingFromInvite="isComingFromInvite"
                :fieldsToHide="fieldsToHide"
                :invitee="invitee"
                @sent="goToNextStep"
                @form-submitted="isFormSubmitted($event)"
            ></register-form>
        </common-content-wrapper>
        <common-content-wrapper width="small">
            <div class="billing-info">
                {{ $t('pages.practice.billing.registration_billing_info') }}
                <a @click="toggleBillingPricingModal">{{ $t('pages.practice.billing.see_plans') }}</a>
            </div>
            <div v-if="!isSignUpWithNpiEnabled && !$route.query.npi" class="find-npi" @click="isSignUpWithNpiEnabled = true">
                <common-icon name="menu-arrow" size="medium" class="find-npi__icon"></common-icon>
                <span>{{ $t('pages.auth.signup.demo.go_back') }}</span>
            </div>
        </common-content-wrapper>
    </auth-layout>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import UserRolesAPI from 'api/UserRolesAPI'
    import SpecialitiesAPI from 'api/SpecialitiesAPI'
    import SignupPageMixin from 'mixins/SignupPageMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import AuthLayout from 'components/layouts/AuthLayout'
    import RegisterForm from 'components/forms/AuthSignupRegisterForm'
    import UserRolesEnum from '@/enums/UserRolesEnum'
    import PhysicianGroupsAPI from 'api/PhysicianGroupsAPI'
    import PricingCards from 'components/page-elements/practice/billing/PricingCards'
    import GoogleAnalyticsService from 'services/GoogleAnalyticsService'
    import JoinInviteTypeEnum from '@/enums/JoinInviteTypeEnum'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import AuthRegisterWithNpiForm from 'components/forms/auth/AuthRegisterWithNpiForm'

    export default {
        metaInfo() {
            if (process.env.VUE_APP_ENV === 'production') {
                return {
                    title: this.$t('pages.auth.signup.title'),
                    meta: [
                        { name: 'description', content: 'pages.auth.signup.page_description' },
                        { name: 'keywords', content: this.$t('pages.auth.signup.page_keywords') },
                        { name: 'author', content: this.$t('pages.auth.signup.page_author') },
                        { name: 'robots', content: this.$t('pages.auth.signup.page_robots') },
                    ],
                }
            }
        },

        mixins: [SignupPageMixin, PageWithDataMixin],

        components: {
            AuthLayout,
            RegisterForm,
            PricingCards,
            AuthRegisterWithNpiForm,
        },

        data() {
            return {
                organizationRoles: [],
                specialities: [],
                physicianGroups: [],
                dataForPrefill: {},
                isComingFromInvite: false,
                isDisabled: false,
                billingPricingModalOpen: false,
                isPagePreparing: false,
                fieldsToHide: [],
                invitee: {},
                JoinInviteTypeEnum,
                isSignUpWithNpiEnabled: true,
            }
        },

        computed: {
            loginLinkClass() {
                return {
                    'login-link__disabled': this.isDisabled,
                }
            },

            getHeading() {
                return this.invitee?.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION
                    ? this.$t('pages.auth.signup.steps.register.heading_outsider')
                    : this.$t('pages.auth.signup.steps.register.heading_two')
            },

            filteredOrganizationRoles() {
                if (this.invitee?.inviteType === JoinInviteTypeEnum.NETWORK) {
                    return this.organizationRoles.filter((role) =>
                        [UserRolesEnum.PHYSICIAN.ID, UserRolesEnum.NURSE_PRACTITIONER.ID].includes(role.data.id),
                    )
                }
                return this.organizationRoles
            },
        },

        methods: {
            isFormSubmitted(event) {
                this.isDisabled = event.loginLinkDisabled ? true : false
            },

            toggleBillingPricingModal() {
                this.billingPricingModalOpen = !this.billingPricingModalOpen
            },

            getPageData() {
                // Fetch specialities first to avoid race condition
                SpecialitiesAPI.getListMultiselect().then((specialities) => {
                    this.specialities = specialities
                    Promise.all([UserRolesAPI.getList(), PhysicianGroupsAPI.getList()])
                        .then(([organizationRoles, physicianGroups]) => {
                            this.organizationRoles = organizationRoles
                            this.physicianGroups = physicianGroups
                        })
                        .finally(() => {
                            this.isPagePreparing = false
                        })
                })
            },

            fetchUsersDataByNpi() {
                ProviderDatabaseAPI.get(this.$route.query.npi).then((res) => {
                    if (!res) return

                    this.dataForPrefill = {
                        firstName: res.firstName,
                        lastName: res.lastName,
                        email: res.email ?? res.businessEmail,
                        phone: res.phone,
                        designation: res.designation,
                        practiceType: res.isPhysician ? PracticeTypeEnum.PHYSICIAN : undefined,
                        userRoleId: res.isPhysician ? UserRolesEnum.PHYSICIAN.ID : undefined,
                        specialities: res.specialities,
                    }
                })
            },

            handleProviderDataMatched(res) {
                this.isSignUpWithNpiEnabled = false
                this.$nextTick(() => {
                    this.dataForPrefill = {
                        firstName: res.firstName,
                        lastName: res.lastName,
                        email: res.email ?? res.businessEmail,
                        phone: res.phone,
                        designation: res.designation,
                        practiceType: res.isPhysician ? PracticeTypeEnum.PHYSICIAN : undefined,
                        userRoleId: res.isPhysician ? UserRolesEnum.PHYSICIAN.ID : undefined,
                        specialities: res.specialities,
                    }
                })
            },

            goWithoutNpi() {
                this.isSignUpWithNpiEnabled = false
                this.$router.push({ name: 'auth.signup.register' })
            },

            generatePhysicianGroupId(invitee) {
                return invitee.inviteType === JoinInviteTypeEnum.PRACTICE
                    ? invitee.physicianGroupId
                    : invitee.practice?.groupId ?? null
            },
        },

        mounted() {
            GoogleAnalyticsService.loadGoogleAnalytics(process.env.VUE_APP_GOOGLE_ANALYTICS)
            this.physicianGroups = [{ label: 'None', value: -1, data: { displayName: 'None', id: -1 } }, ...this.physicianGroups]
            const joinInviteToken = Object.keys(this.$route.query)
                .filter((key) => /^.*jit$/.test(key))
                .map((key) => this.$route.query[key])[0]
            if (joinInviteToken && /^[0-9A-Fa-f]+$/.test(joinInviteToken)) {
                this.isSignUpWithNpiEnabled = false
                UsersAPI.getJoinInviteByToken(joinInviteToken, [
                    'id',
                    'toEmail',
                    'practiceType',
                    'practiceName',
                    'practice(id,name,practiceType,groupId)',
                    'userRoleId',
                    'inviteType',
                    'fromUserRoleId',
                    'firstName',
                    'lastName',
                    'designation',
                    'specialities',
                    'physicianGroupId',
                ])
                    .then((res) => {
                        this.invitee = res
                        const dataForPrefill = {
                            joinInviteToken,
                            email: res.toEmail,
                            firstName: res.firstName,
                            lastName: res.lastName,
                            practiceName: res.practiceName,
                            userRoleId: res.userRoleId,
                            physicianGroupId: this.generatePhysicianGroupId(res),
                            designation: res.designation,
                            specialities: res.specialities,
                        }
                        if (res.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION) {
                            this.fieldsToHide = ['phone']
                        }
                        if (res.practice) {
                            dataForPrefill.practiceName =
                                res.inviteType === JoinInviteTypeEnum.REFERRAL_CREATION ? undefined : res.practice.name
                            dataForPrefill.practiceId = res.practice.id
                        }
                        if (res.practiceType) {
                            dataForPrefill.practiceType = res.practiceType
                        }
                        this.dataForPrefill = dataForPrefill
                        this.isComingFromInvite = true
                    })
                    .catch(() => {})
            }

            if (this.$route.query.npi) {
                this.isSignUpWithNpiEnabled = false
                this.fetchUsersDataByNpi()
            }
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = 'mobile'
        },

        beforeDestroy() {
            //THIS SHOULD BE REMOVED WHEN RESPONSIVE IS IMPLEMENTED
            document.body.className = ''
        },
        created() {
            this.getPageData()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    .login {
        &-link {
            &__disabled {
                pointer-events: none;
                opacity: 0.2;
            }
        }
    }

    .find-npi {
        text-align: center;
        cursor: pointer;
        color: $color--accent-1;
        margin-top: 40px;

        &__icon {
            rotate: 180deg;
        }
    }
</style>
